import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface PanelHandle {
  panel: React.RefObject<HTMLDivElement | null>;
  trigger: React.RefObject<HTMLDivElement | null>;
}

/**
 * Every open floating panel, in the order they opened.
 *
 * Module-level, because the question a panel has to answer on an outside click — *did this
 * land in a panel that opened from inside me?* — cannot be answered from inside one panel. A
 * nested panel is a portal at the document root, so it is nowhere near its owner in the DOM
 * however deeply the components nest.
 */
const openPanels: PanelHandle[] = [];

/**
 * Whether `target` belongs to `self` — its own panel and trigger, plus the panels of anything
 * that opened from inside it, transitively.
 *
 * A panel opened LATER whose trigger sits in a region we already own is ours: that is what
 * makes a `<Select>` in a `<Calendar>` in a `<DateField>` resolve, and it extends to any depth
 * without the panels knowing about each other.
 */
function ownsTarget(self: PanelHandle, target: Node): boolean {
  const regions: Node[] = [];
  if (self.panel.current) regions.push(self.panel.current);
  if (self.trigger.current) regions.push(self.trigger.current);

  const start = openPanels.indexOf(self);

  if (start !== -1) {
    for (let i = start + 1; i < openPanels.length; i += 1) {
      const later = openPanels[i];
      const laterTrigger = later.trigger.current;
      const laterPanel = later.panel.current;

      if (laterTrigger && laterPanel && regions.some((region) => region.contains(laterTrigger))) {
        regions.push(laterPanel);
      }
    }
  }

  return regions.some((region) => region.contains(target));
}

/**
 * Positions a dropdown panel — reference §5.
 *
 * Four rules, and each exists because the naive version breaks somewhere real:
 *  - `position: fixed`, so the panel escapes a scrolling table or modal's overflow.
 *  - it follows the trigger on scroll, rather than detaching and floating over the page.
 *  - it never closes on a scroll that happens INSIDE the panel itself.
 *  - **it never closes on a click inside a panel that opened from within it.** Every floating
 *    panel portals to the document root, so a nested one is not a DOM descendant of its owner
 *    — which made every click on a nested option an "outside" click. The calendar's month and
 *    year pickers are the case: the calendar closed on `mousedown` and unmounted the `<Select>`
 *    before its option's `click` could fire, so the dropdown appeared to do nothing at all.
 *    Nothing errored, and the value simply never changed.
 */
export function useFloatingPanel(open: boolean, onClose: () => void) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // One identity for the lifetime of the hook, so the registry can match it on cleanup.
  const handleRef = useRef<PanelHandle>({ panel: panelRef, trigger: triggerRef });

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const bounds = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bounds.bottom;
    const panelHeight = panelRef.current?.offsetHeight ?? 280;
    const openUpward = spaceBelow < panelHeight && bounds.top > spaceBelow;

    setRect({
      top: openUpward ? Math.max(8, bounds.top - panelHeight - 4) : bounds.bottom + 4,
      left: bounds.left,
      width: bounds.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;

    const self = handleRef.current;
    openPanels.push(self);

    function onScroll(event: Event) {
      // A scroll inside the panel — or inside one opened from it — must not close it. That
      // would make a long option list unusable with a trackpad.
      if (ownsTarget(self, event.target as Node)) {
        return;
      }

      reposition();
    }

    function onPointerDown(event: MouseEvent) {
      if (!ownsTarget(self, event.target as Node)) {
        onClose();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;

      /*
        Only the innermost panel closes. Escape in a picker that opened from a calendar should
        return the reader to the calendar, not dismiss both — and before the registry existed
        there was no way to know which panel the reader meant.
      */
      if (openPanels[openPanels.length - 1] !== self) return;

      event.stopPropagation();
      onClose();
    }

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      const index = openPanels.indexOf(self);
      if (index !== -1) openPanels.splice(index, 1);

      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, reposition]);

  return { triggerRef, panelRef, rect };
}
